import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Pagination, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { DebitNoteService } from '../../services/debitNote.service';
import { DebitNote } from '../../interfaces/procurement/debitNote.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

export const DebitNoteList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SUPPLIER_BILL_MANAGE);
  const [notes, setNotes] = useState<DebitNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const load = async (page = 0) => {
    setLoading(true);
    try {
      const r = await DebitNoteService.listByInstitution(page, 10);
      setNotes(r.content);
      setPageInfo({ currentPage: r.number, totalPages: r.totalPages, totalElements: r.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load debit notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.debitNotes.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Debit Notes</Typography>
        {canManage && (
          <Button data-help="procurement.debitNotes.new" variant="contained" onClick={() => navigate('/procurement/debit-notes/new')}>New Debit Note</Button>
        )}
      </Box>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>
          <Paper data-help-section="procurement.debitNotes.table">
            <ScrollableTable>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell data-help="procurement.debitNotes.col.number">Debit Note</TableCell>
                    <TableCell data-help="procurement.debitNotes.col.bill">Bill</TableCell>
                    <TableCell data-help="procurement.debitNotes.col.supplier">Supplier</TableCell>
                    <TableCell data-help="procurement.debitNotes.col.date">Date</TableCell>
                    <TableCell data-help="procurement.debitNotes.col.total" align="right">Total</TableCell>
                    <TableCell data-help="procurement.debitNotes.col.unapplied" align="right">Unapplied</TableCell>
                    <TableCell data-help="procurement.debitNotes.col.status">Status</TableCell>
                    <TableCell data-help="procurement.debitNotes.col.actions" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map(n => (
                    <TableRow key={n.id}>
                      <TableCell>{n.debitNoteNumber}</TableCell>
                      <TableCell>{n.billNumber ?? '—'}</TableCell>
                      <TableCell>{n.supplierName ?? '—'}</TableCell>
                      <TableCell>{formatDate(n.noteDate)}</TableCell>
                      <TableCell align="right">{formatMoney(n.totalAmount)}</TableCell>
                      <TableCell align="right">{formatMoney(n.unappliedAmount)}</TableCell>
                      <TableCell>
                        <Chip data-help="procurement.debitNotes.status" size="small" label={humanizeEnum(n.status)} color={n.status === 'POSTED' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View" arrow>
                          <IconButton data-help="procurement.debitNotes.action.view" size="small" color="primary" aria-label="View" onClick={() => navigate(`/procurement/debit-notes/${n.id}`)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {notes.length === 0 && (
                    <TableRow><TableCell colSpan={8}>No debit notes yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollableTable>
          </Paper>
          {pageInfo.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={pageInfo.totalPages} page={pageInfo.currentPage + 1} color="primary"
                onChange={(_e, p) => load(p - 1)} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DebitNoteList;
